/* byChapters Help Centre: FAQ filtering and deep links, Help Centre search, and the "On this page" list. */
(() => {
  const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9£]+/g, ' ').trim();
  const words = (s) => norm(s).split(' ').filter(Boolean);
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  // Terms match the start of a word, so "led" finds "LED" but not "assembled".
  const has = (text, w) => text.includes(` ${w}`);
  // A light stemmer so "batteries" finds "battery", "lights" finds "light" and "returning" finds "returns". The index
  // and the query both go through it, and the raw text is still checked, so prefixes keep working either way.
  const stem = (w) => {
    if (w.length < 4 || /\d/.test(w)) return w;
    if (w.endsWith('ies')) return `${w.slice(0, -3)}y`;
    if (/(x|z|ch|sh|ss)es$/.test(w)) return w.slice(0, -2);
    if (w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
    let s = w;
    if (w.length > 5 && w.endsWith('ing')) s = w.slice(0, -3);
    else if (w.length > 4 && w.endsWith('ed')) s = w.slice(0, -2);
    return s === w ? w : s.replace(/([b-df-hj-np-tv-z])\1$/, '$1');
  };
  const stems = (s) => words(s).map(stem).join(' ');
  // Words customers use for things the FAQ says differently. Each alternative is matched like a typed word.
  const SYN = Object.fromEntries(Object.entries({
    shipping: ['delivery', 'dispatch'],
    postage: ['delivery'],
    courier: ['royal mail'],
    carrier: ['royal mail'],
    eta: ['dispatch', 'delivery'],
    arrival: ['arrive', 'delivery'],
    delay: ['late', 'longer'],
    stolen: ['lost', 'missing'],
    international: ['outside the uk'],
    abroad: ['outside the uk'],
    overseas: ['outside the uk'],
    worldwide: ['outside the uk'],
    europe: ['outside the uk'],
    european: ['outside the uk'],
    eu: ['outside the uk'],
    ireland: ['outside the uk'],
    preorder: ['pre order'],
    available: ['stock'],
    availability: ['stock'],
    sold: ['stock'],
    instructions: ['manual'],
    guide: ['manual'],
    booklet: ['manual'],
    pdf: ['digital copy', 'manual'],
    download: ['digital copy', 'manual'],
    assembly: ['assembled', 'build'],
    included: ['in the box'],
    include: ['in the box'],
    beginner: ['first build', 'difficulty'],
    easy: ['difficulty', 'first build'],
    easiest: ['difficulty', 'first build'],
    simple: ['difficulty', 'first build'],
    hard: ['difficulty'],
    lights: ['led'],
    lamp: ['led', 'light'],
    bulb: ['led', 'light'],
    power: ['batteries', 'led'],
    working: ['turn on', 'switch'],
    broken: ['faulty', 'damaged', 'turn on'],
    usb: ['batteries'],
    plug: ['batteries'],
    tools: ['tweezers', 'knife', 'glue'],
    adhesive: ['glue'],
    material: ['wooden', 'wood'],
    mdf: ['wooden'],
    plywood: ['wooden'],
    dimensions: ['size', 'cm'],
    measurements: ['size', 'cm'],
    kids: ['age', 'younger', 'adult'],
    child: ['age', 'younger', 'adult'],
    children: ['age', 'younger', 'adult'],
    teen: ['age', 'younger', 'adult'],
    teenager: ['age', 'younger', 'adult'],
    warranty: ['faulty', 'put it right', 'replacement'],
    guarantee: ['faulty', 'put it right', 'replacement'],
    cancellation: ['cancel'],
    exchange: ['replacement', 'return'],
    swap: ['replacement', 'return'],
    voucher: ['points', '£10 off'],
    coupon: ['points', '£10 off'],
    promo: ['points', '£10 off'],
    discount: ['points', '£10 off'],
    code: ['points', '£10 off'],
    reward: ['points'],
    loyalty: ['points', 'rewards'],
    login: ['account', 'my shelf'],
    sign: ['account', 'my shelf'],
    password: ['account', 'my shelf'],
    register: ['account', 'my shelf'],
    phone: ['chat', 'get in touch'],
    telephone: ['chat', 'get in touch'],
    call: ['chat', 'get in touch'],
    tax: ['vat'],
    paypal: ['payment'],
    klarna: ['payment'],
    card: ['payment'],
  }).map(([k, v]) => [stem(k), v]));
  // Filler words are dropped from a query ("where is my order" → "order") unless nothing else would be left.
  const STOP = new Set('a an and are be can could d do does doesn don for how i if in is isn it ll m me my no not of on or re s should t the there this to ve we what when where which who why will with won would you your'.split(' '));
  const query = (s) => {
    const all = words(s);
    const kept = all.filter((w) => !STOP.has(w));
    return kept.length ? kept : all;
  };
  const makeIndex = (text) => ({ raw: ` ${norm(text)}`, stem: ` ${stems(text)}` });
  // A query word matches when it, or one of its alternatives, starts a word in the raw text or in the stemmed text.
  const hit = (ix, w) => [w].concat(SYN[stem(w)] || SYN[w] || []).some((a) => has(ix.raw, a) || has(ix.stem, stems(a)));

  // Highlight the in-page link for whichever section is on screen.
  const spy = (links, targets) => {
    if (!('IntersectionObserver' in window) || !targets.length) return;
    const byId = new Map(links.map((a) => [decodeURIComponent(a.hash.slice(1)), a]));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const link = byId.get(entry.target.id);
        if (!link) return;
        links.forEach((a) => a.removeAttribute('aria-current'));
        link.setAttribute('aria-current', 'true');
      });
    }, { rootMargin: '-15% 0px -70% 0px' });
    targets.forEach((t) => io.observe(t));
  };

  // FAQ page: live filter, ?q= prefill and #question deep links.
  document.querySelectorAll('[data-bch-faq]').forEach((faq) => {
    const root = faq.closest('.bch');
    const input = root.querySelector('[data-bch-search] input');
    const status = root.querySelector('[data-bch-status]');
    const empty = root.querySelector('[data-bch-empty]');
    const items = [...faq.querySelectorAll('details')];
    const cats = [...faq.querySelectorAll('.bch-cat')];
    const ix = new Map(items.map((d) => [d, makeIndex(d.textContent)]));

    const filter = () => {
      const terms = query(input ? input.value : '');
      let shown = 0;
      items.forEach((d) => {
        const match = !terms.length || terms.every((w) => hit(ix.get(d), w));
        d.hidden = !match;
        if (match) shown += 1;
      });
      if (terms.length && shown <= 3) items.forEach((d) => { if (!d.hidden) d.open = true; });
      cats.forEach((c) => { c.hidden = terms.length > 0 && !c.querySelector('details:not([hidden])'); });
      if (empty) empty.hidden = shown > 0;
      if (status) status.textContent = terms.length ? plural(shown, 'question matches', 'questions match') : '';
    };

    const openFromHash = () => {
      const id = decodeURIComponent(location.hash.slice(1));
      const target = id && document.getElementById(id);
      if (!target || target.tagName !== 'DETAILS') return;
      if (input && input.value) { input.value = ''; filter(); }
      target.open = true;
      target.classList.remove('is-target');
      void target.offsetWidth;
      target.classList.add('is-target');
      target.scrollIntoView({ block: 'start' });
    };

    if (input) {
      input.addEventListener('input', filter);
      input.addEventListener('keydown', (e) => { if (e.key === 'Escape') { input.value = ''; filter(); } });
      const q = new URLSearchParams(location.search).get('q');
      if (q) { input.value = q; filter(); }
    }
    openFromHash();
    window.addEventListener('hashchange', openFromHash);

    const navLinks = [...root.querySelectorAll('.bch-side .bch-nav a[href^="#"]')];
    spy(navLinks, cats);
  });

  // Help Centre: instant answers from the FAQ as you type.
  document.querySelectorAll('[data-bch-search="hub"]').forEach((box) => {
    const root = box.closest('.bch');
    const input = box.querySelector('input');
    const results = box.querySelector('.bch-results');
    const status = root.querySelector('[data-bch-status]');
    const faqUrl = box.dataset.faqUrl || '/pages/faq';
    let index = [];
    try { index = JSON.parse(document.querySelector('[data-bch-index]').textContent); } catch (e) { index = []; }
    index.forEach((it) => { it.all = makeIndex(`${it.q} ${it.a} ${it.c}`); it.title = makeIndex(it.q); });
    const allUrl = () => `${faqUrl}?q=${encodeURIComponent(input.value.trim())}`;

    const render = () => {
      const terms = query(input.value);
      results.textContent = '';
      if (!terms.length) { results.hidden = true; if (status) status.textContent = ''; return; }
      const hits = index
        .filter((it) => terms.every((w) => hit(it.all, w)))
        .map((it) => ({ it, score: terms.reduce((s, w) => s + (hit(it.title, w) ? 2 : 1), 0) }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 6);
      const list = document.createElement('ul');
      hits.forEach(({ it }) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = it.u;
        const cat = document.createElement('span');
        cat.className = 'bch-results__cat';
        cat.textContent = it.c;
        const q = document.createElement('b');
        q.textContent = it.q;
        const snippet = document.createElement('small');
        snippet.textContent = it.a.length > 120 ? `${it.a.slice(0, 117).trim()}…` : it.a;
        a.append(cat, q, snippet);
        li.append(a);
        list.append(li);
      });
      results.append(list);
      const all = document.createElement('a');
      all.className = 'bch-results__all';
      all.href = allUrl();
      all.textContent = hits.length ? 'See every matching question →' : 'No instant answer. Search the full FAQ →';
      results.append(all);
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      if (status) status.textContent = hits.length ? plural(hits.length, 'suggested answer', 'suggested answers') : 'No suggested answers';
    };
    const close = () => { results.hidden = true; input.setAttribute('aria-expanded', 'false'); };

    input.addEventListener('input', render);
    input.addEventListener('focus', () => { if (input.value.trim()) render(); });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && input.value.trim()) { e.preventDefault(); location.href = allUrl(); }
      if (e.key === 'Escape') { input.value = ''; render(); }
      if (e.key === 'ArrowDown') { const first = results.querySelector('a'); if (first) { e.preventDefault(); first.focus(); } }
    });
    results.addEventListener('keydown', (e) => {
      const links = [...results.querySelectorAll('a')];
      const i = links.indexOf(document.activeElement);
      if (e.key === 'ArrowDown' && i < links.length - 1) { e.preventDefault(); links[i + 1].focus(); }
      if (e.key === 'ArrowUp') { e.preventDefault(); (i > 0 ? links[i - 1] : input).focus(); }
      if (e.key === 'Escape') { close(); input.focus(); }
    });
    /* Clicks outside the box close the results, except the Popular chips, which fill the box and must leave them open. */
    document.addEventListener('click', (e) => {
      const t = e.target;
      if (!box.contains(t) && !(t.closest && t.closest('[data-bch-term]'))) close();
    });
    root.querySelectorAll('[data-bch-term]').forEach((chip) => {
      chip.addEventListener('click', () => { input.value = chip.dataset.bchTerm; render(); input.focus(); });
    });
  });

  // Phones: the help topics menu scrolls sideways, so bring the current page into view.
  document.querySelectorAll('.bch-nav ul').forEach((list) => {
    const current = list.querySelector('[aria-current="page"]');
    if (!current || list.scrollWidth <= list.clientWidth) return;
    list.scrollLeft += current.getBoundingClientRect().left - list.getBoundingClientRect().left - 20;
  });

  // Help pages: build "On this page" from the headings.
  document.querySelectorAll('[data-bch-toc]').forEach((toc) => {
    const article = toc.closest('.bch').querySelector('.bch-article');
    const headings = article ? [...article.querySelectorAll('h2')] : [];
    if (headings.length < 3) return;
    const list = toc.querySelector('ol');
    headings.forEach((h) => {
      if (!h.id) h.id = norm(h.textContent).replace(/ /g, '-').slice(0, 60);
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${h.id}`;
      a.textContent = h.textContent.trim().replace(/^\d+\.\s*/, '');
      li.append(a);
      list.append(li);
    });
    toc.hidden = false;
    spy([...list.querySelectorAll('a')], headings);
  });
})();
